import { useEffect, useRef, useState } from 'react';

interface HoverTarget {
  id: string;
  el: HTMLElement;
}

/**
 * Même machine que l’agenda : fondu sortant, échange invisible, fondu entrant.
 * Une seule carte pour toute la liste.
 */
export function useTaskListPreview(paused: boolean) {
  const [hover, setHover] = useState<HoverTarget | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [shown, setShown] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const token = useRef(0);
  const previewShown = useRef(false);
  const queued = useRef<HoverTarget | null>(null);

  const clearHide = () => {
    if (hideTimer.current != null) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const reveal = (myToken: number) => {
    const next = queued.current;
    queued.current = null;
    if (!next || token.current !== myToken) return;
    setHover(next);
    setRect(next.el.getBoundingClientRect());
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (token.current !== myToken) return;
        previewShown.current = true;
        setShown(true);
      });
    });
  };

  const fadeOut = (myToken: number) => {
    previewShown.current = false;
    setShown(false);
    clearHide();
    hideTimer.current = setTimeout(() => {
      if (token.current !== myToken) return;
      if (queued.current) {
        reveal(myToken);
        return;
      }
      setHover(null);
      setRect(null);
      hideTimer.current = null;
    }, 90);
  };

  useEffect(() => () => clearHide(), []);

  useEffect(() => {
    if (!paused) return;
    queued.current = null;
    clearHide();
    previewShown.current = false;
    token.current += 1;
    setShown(false);
    setHover(null);
    setRect(null);
  }, [paused]);

  useEffect(() => {
    if (!hover) return;
    const sync = () => setRect(hover.el.getBoundingClientRect());
    window.addEventListener('scroll', sync, true);
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('resize', sync);
    };
  }, [hover]);

  return {
    hoverId: hover?.id ?? null,
    rect,
    shown,
    onEnter: (id: string, el: HTMLElement) => {
      if (paused) return;
      if (previewShown.current && hover?.id === id) {
        setHover({ id, el });
        setRect(el.getBoundingClientRect());
        return;
      }
      queued.current = { id, el };
      if (previewShown.current) {
        fadeOut(++token.current);
        return;
      }
      if (hover) return;
      reveal(++token.current);
    },
    onLeave: () => {
      queued.current = null;
      if (!previewShown.current && !hover) return;
      fadeOut(++token.current);
    },
  };
}
